import { useEditorT } from '@i18n/editor';
import { editorUrl } from '@/lib/editorBasePath';
import { isRealTap, recordTapStart } from '@/lib/tapGuard';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Upload, ChevronDown, ChevronRight, Loader2, ImagePlus, Trash2, Heart, AlertTriangle, FolderPlus, Check, FileText, Search, X } from 'lucide-react';
import { uploadBatch } from '@/lib/directUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AllFolder } from '@/hooks/useBookLoader';

interface PictureQuality {
  level: 'ok' | 'warn' | 'bad';
}

interface Picture {
  id: number;
  thumb_url: string;
  full_url: string;
  width: number;
  height: number;
  ratio: number;
  force?: boolean;
  quality?: PictureQuality;
  description?: string;
}

interface Subfolder {
  id: number;
  title: string;
  pictures_count: number;
  pictures: Picture[];
}

interface Folder {
  id: number;
  title: string;
  pictures_count: number;
  pictures: Picture[];
  subfolders?: Subfolder[];
}

interface PhotosPanelProps {
  onAddImage: (url: string, ratio?: number, pictureId?: number, thumbUrl?: string) => void;
  onAddText: (variant?: 'title' | 'subtitle' | 'text', customContent?: string) => void;
  pictureToUse: Set<number>;
  allFolders: AllFolder[];
  /** true quand l'onglet Photos est visible (le panneau reste monté en permanence) */
  isActive?: boolean;
  /** Ouvre l'import dès le premier montage, sans attendre le chargement des dossiers. */
  autoOpenImport?: boolean;
  /** wememberz : pré-sélectionne le premier dossier dans la popup d'import */
  isWememberz?: boolean;
}

const QualityBadge: React.FC<{ quality: PictureQuality }> = ({ quality }) => {
  const { te } = useEditorT();
  if (quality.level === 'ok') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 border border-white/80" />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{te("Qualité OK")}</TooltipContent>
      </Tooltip>
    );
  }
  if (quality.level === 'warn') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-orange-500 border border-white/80" />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{te("Qualité moyenne")}</TooltipContent>
      </Tooltip>
    );
  }
  if (quality.level === 'bad') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="absolute bottom-0.5 right-0.5 flex items-center justify-center w-4 h-4">
            <AlertTriangle size={14} className="text-red-500 drop-shadow" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{te("Qualité insuffisante")}</TooltipContent>
      </Tooltip>
    );
  }
  return null;
};



const FOLDER_PAGE_SIZE = 6;

const PhotosPanel: React.FC<PhotosPanelProps> = ({ onAddImage, onAddText, pictureToUse, allFolders, isActive = false, autoOpenImport = false, isWememberz = false }) => {
  const { te } = useEditorT();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [foldersHasMore, setFoldersHasMore] = useState(true);
  const [foldersOffset, setFoldersOffset] = useState(0);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [expandedSubfolders, setExpandedSubfolders] = useState<Set<string>>(new Set());
  const [fullPictures, setFullPictures] = useState<Record<number, Picture[]>>({});
  const [fullSubPictures, setFullSubPictures] = useState<Record<string, Picture[]>>({});
  const [loadingFolder, setLoadingFolder] = useState<number | null>(null);
  const [loadingSubfolder, setLoadingSubfolder] = useState<string | null>(null);
  const foldersSentinelRef = useRef<HTMLDivElement | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedFolders, setSearchedFolders] = useState<Record<number, Folder>>({});
  const [loadingSearchFolder, setLoadingSearchFolder] = useState<number | null>(null);

  // Import popup states
  const [importOpen, setImportOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Picture | null>(null);
  const [deleting, setDeleting] = useState(false);
  const importFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoOpenImport) setImportOpen(true);
  }, [autoOpenImport]);

  // New folder creation states
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolderLoading, setCreatingFolderLoading] = useState(false);
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  // Folders created during the session (not yet in allFolders from load_book)
  const [newlyCreatedFolders, setNewlyCreatedFolders] = useState<AllFolder[]>([]);

  // Local force state to reflect toggling without refetching
  const [forceOverrides, setForceOverrides] = useState<Record<number, boolean>>({});

  const loadMoreFolders = useCallback((offset: number) => {
    if (loading) return;
    setLoading(true);
    fetch(editorUrl(`/load_folders?offset=${offset}`))
      .then((r) => r.json())
      .then((data: Folder[]) => {
        setFolders((prev) => [...prev, ...data]);
        setExpandedFolders((prev) => {
          const next = new Set(prev);
          data.forEach((f) => next.add(f.id));
          return next;
        });
        setFoldersOffset(offset + data.length);
        setFoldersHasMore(data.length >= FOLDER_PAGE_SIZE);
      })
      .catch((err) => console.warn('[PhotosPanel] Failed to load folders:', err))
      .finally(() => setLoading(false));
  }, [loading]);

  useEffect(() => {
    if (loaded) return;
    setLoaded(true);
    loadMoreFolders(0);
  }, [loaded]);

  // Si aucune photo n'existe (dossiers vides ou inexistants), ouvrir directement
  // la popup d'import — à chaque (ré)ouverture de l'onglet Photos, ou au clic
  // sur un slot vide (événement émis par Canvas).
  const autoOpenHandledRef = useRef(false);
  const countFolderPictures = (f: Folder | AllFolder) =>
    (f.pictures_count ?? 0) +
    ((f as Folder).subfolders ?? []).reduce((sum, s) => sum + (s.pictures_count ?? 0), 0);

  const openImportIfNoPictures = useCallback(() => {
    const totalPictures =
      allFolders.reduce((sum, f) => sum + countFolderPictures(f), 0) +
      folders.reduce((sum, f) => sum + countFolderPictures(f), 0);
    if (totalPictures === 0) {
      handleOpenImport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFolders, folders]);

  useEffect(() => {
    if (!isActive) {
      autoOpenHandledRef.current = false;
      return;
    }
    if (autoOpenHandledRef.current) return;
    if (!loaded || loading) return;
    if (foldersHasMore) return;
    autoOpenHandledRef.current = true;
    const totalPictures =
      allFolders.reduce((sum, f) => sum + countFolderPictures(f), 0) +
      folders.reduce((sum, f) => sum + countFolderPictures(f), 0);
    if (totalPictures === 0) {
      handleOpenImport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, loaded, loading, folders, foldersHasMore, allFolders]);

  // Ouverture forcée (pictures_count === 0 au chargement)
  useEffect(() => {
    const handler = () => handleOpenImport();
    window.addEventListener('yearbook:open-import', handler);
    return () => window.removeEventListener('yearbook:open-import', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clic sur un slot vide alors qu'il n'y a aucune photo → ouvrir l'import
  useEffect(() => {
    const handler = () => openImportIfNoPictures();
    window.addEventListener('yearbook:open-import-if-empty', handler);
    return () => window.removeEventListener('yearbook:open-import-if-empty', handler);
  }, [openImportIfNoPictures]);

  // Build search results from the complete allFolders list.
  // Folders already paginated into `folders` reuse their data (pictures, subfolders).
  // Others are created as stubs and loaded on demand when expanded.
  useEffect(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) {
      setSearchedFolders({});
      return;
    }
    const next: Record<number, Folder> = {};
    allFolders
      .filter((f) => f.title.toLowerCase().includes(trimmed))
      .forEach((f) => {
        const existing = folders.find((folder) => folder.id === f.id);
        if (existing) {
          next[f.id] = existing;
        } else {
          next[f.id] = {
            id: f.id,
            title: f.title,
            pictures_count: f.pictures_count ?? 0,
            pictures: [],
            subfolders: [],
          };
        }
      });
    setSearchedFolders(next);
  }, [searchQuery, folders, allFolders]);

  // IntersectionObserver for folders infinite scroll (disabled while searching)
  useEffect(() => {
    const sentinel = foldersSentinelRef.current;
    if (!sentinel || !foldersHasMore || loading || searchQuery.trim()) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          loadMoreFolders(foldersOffset);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [foldersHasMore, loading, foldersOffset, loadMoreFolders]);

  const loadSearchFolderPictures = useCallback((folderId: number) => {
    setLoadingSearchFolder(folderId);
    fetch(editorUrl(`/load_pictures_for_folder?id=${folderId}`))
      .then((r) => r.json())
      .then((data: Picture[]) => {
        setSearchedFolders((prev) => ({
          ...prev,
          [folderId]: { ...prev[folderId], pictures: data, pictures_count: data.length },
        }));
      })
      .catch((err) => console.warn('[PhotosPanel] Failed to load search folder pictures:', err))
      .finally(() => setLoadingSearchFolder(null));
  }, []);

  const toggleFolder = useCallback((id: number) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      const willExpand = !next.has(id);
      if (willExpand) {
        next.add(id);
        // If this folder comes from search results and has no pictures loaded yet, fetch them.
        const stub = searchedFolders[id];
        if (stub && stub.pictures.length === 0 && stub.pictures_count > 0 && loadingSearchFolder !== id) {
          loadSearchFolderPictures(id);
        }
      } else {
        next.delete(id);
      }
      return next;
    });
  }, [searchedFolders, loadingSearchFolder, loadSearchFolderPictures]);

  const toggleSubfolder = useCallback((folderId: number, subId: number) => {
    const key = `${folderId}:${subId}`;
    setExpandedSubfolders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const loadAllPictures = useCallback((folderId: number) => {
    setLoadingFolder(folderId);
    fetch(editorUrl(`/load_pictures_for_folder?id=${folderId}`))
      .then((r) => r.json())
      .then((data: Picture[]) => {
        setFullPictures((prev) => ({ ...prev, [folderId]: data }));
      })
      .catch((err) => console.warn('[PhotosPanel] Failed to load pictures:', err))
      .finally(() => setLoadingFolder(null));
  }, []);

  const loadAllSubPictures = useCallback((folderId: number, subId: number) => {
    const key = `${folderId}:${subId}`;
    setLoadingSubfolder(key);
    fetch(editorUrl(`/load_pictures_for_folder?id=${subId}`))
      .then((r) => r.json())
      .then((data: Picture[]) => {
        setFullSubPictures((prev) => ({ ...prev, [key]: data }));
      })
      .catch((err) => console.warn('[PhotosPanel] Failed to load subfolder pictures:', err))
      .finally(() => setLoadingSubfolder(null));
  }, []);

  const handleToggleFavori = useCallback((pic: Picture) => {
    const currentForce = forceOverrides[pic.id] ?? pic.force ?? false;
    const newForce = !currentForce;
    setForceOverrides((prev) => ({ ...prev, [pic.id]: newForce }));
    fetch(editorUrl('/add_photo_to_favori'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ picture_id: pic.id }),
    }).catch((err) => console.warn('[PhotosPanel] Failed to toggle favori:', err));
  }, [forceOverrides]);

  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolderLoading(true);
    try {
      const res = await fetch(editorUrl('/create_event'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ name: newFolderName.trim() }),
      });
      const data = await res.json();
      const newFolder: Folder = { id: data.id, title: data.name, pictures_count: 0, pictures: [] };
      setFolders((prev) => [newFolder, ...prev]);
      setNewlyCreatedFolders((prev) => [{ id: data.id, title: data.name }, ...prev]);
      setSelectedFolderId(String(data.id));
      setCreatingFolder(false);
      setNewFolderName('');
    } catch (err) {
      console.warn('[PhotosPanel] Failed to create folder:', err);
    } finally {
      setCreatingFolderLoading(false);
    }
  }, [newFolderName]);

  // wememberz : le 1er dossier de la liste est présélectionné dans la popup d'import
  const defaultFolderId = useCallback(
    () => (isWememberz && allFolders.length > 0 ? String(allFolders[0].id) : ''),
    [isWememberz, allFolders]
  );

  const handleOpenImport = () => {
    setSelectedFolderId(defaultFolderId());
    setSelectedFiles([]);
    setUploading(false);
    setUploadProgress(0);
    setUploadDone(false);
    setCreatingFolder(false);
    setNewFolderName('');
    setImportOpen(true);
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
      e.target.value = '';
    }
  };

  const addDroppedFiles = useCallback((files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/') || /\.(png|jpe?g|heic|heif|webp|gif)$/i.test(f.name));
    if (images.length > 0) setSelectedFiles((prev) => [...prev, ...images]);
  }, []);

  const [dialogDragOver, setDialogDragOver] = useState(false);

  const handleUpload = async () => {
    if (!selectedFolderId || selectedFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    const { failed } = await uploadBatch(selectedFiles, selectedFolderId, {
      concurrency: 4, // commence à 1 ou 2 (beaucoup plus fiable)
      onProgress: (done) => setUploadProgress(done),
      onFileDone: (r) => {
        if (!r.ok) {
          console.warn("[Upload] failed:", r.file.name, 'step' in r ? r.step : '', 'error' in r ? r.error : '');
          // ici tu peux afficher un toast / message si tu veux
        }
      },
    });
    
    if (failed.length) {
      // Option: tu peux afficher un résumé et NE PAS fermer le popup
      console.warn("Uploads échoués:", failed.map(f => f.file.name));
      // return; // si tu veux empêcher la fermeture auto en cas d'échec
    }

    setUploading(false);
    setUploadDone(true);
    setTimeout(() => {
      setImportOpen(false);
      setLoaded(false);
      setFolders([]);
      setFoldersOffset(0);
      setFoldersHasMore(true);
      setFullPictures({});
    }, 800);
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    setLoaded(false);
    setFolders([]);
    setFoldersOffset(0);
    setFoldersHasMore(true);
    setFullPictures({});
  };

  const handleDeletePicture = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await fetch(`/pictures/${deleteTarget.id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      setFolders((prev) =>
        prev.map((f) => ({
          ...f,
          pictures: f.pictures.filter((p) => p.id !== deleteTarget.id),
          pictures_count: f.pictures.filter((p) => p.id !== deleteTarget.id).length !== f.pictures.length ? f.pictures_count - 1 : f.pictures_count,
        }))
      );
      setFullPictures((prev) => {
        const next = { ...prev };
        for (const key in next) {
          next[key] = next[key].filter((p) => p.id !== deleteTarget.id!);
        }
        return next;
      });
    } catch (err) {
      console.warn('[PhotosPanel] Failed to delete picture:', err);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  

  const isPicForce = (pic: Picture) => forceOverrides[pic.id] ?? pic.force ?? false;

  const handlePicturePointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>, pic: Picture) => {
    if (e.pointerType === 'mouse') return;
    if (!isRealTap(e)) return; // le doigt a glissé : c'était un scroll, pas un appui
    e.preventDefault();
    e.stopPropagation();
    onAddImage(pic.full_url, pic.width / pic.height, pic.id, pic.thumb_url);
  }, [onAddImage]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-3">
        <button
          type="button"
          onClick={handleOpenImport}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
          onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
            const files = Array.from(e.dataTransfer.files).filter(f => /\.(png|jpe?g)$/i.test(f.name));
            if (files.length > 0) {
              setSelectedFiles(files);
              setSelectedFolderId(defaultFolderId());
              setUploadProgress(0);
              setUploadDone(false);
              setUploading(false);
              setImportOpen(true);
            }
          }}
          className="w-full rounded-lg border-2 border-dashed border-editor-sidebar-border p-3 text-center transition-colors cursor-pointer hover:border-primary/60 hover:bg-primary/5"
        >
          <Upload size={18} className="mx-auto mb-1.5 text-muted-foreground" />
          <p className="text-xs font-medium text-editor-sidebar-foreground">{te("Ajouter vos photos")}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{te("Glissez-déposez (png, jpg, jpeg)")}</p>
          <p className="text-[10px] text-muted-foreground">{te("ou cliquez pour importer depuis votre ordinateur")}</p>
        </button>

        {/* Search albums */}
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={te("Rechercher un album...")}
            className="w-full h-9 pl-8 pr-8 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={te("Effacer la recherche")}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Import Dialog */}
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="max-w-3xl w-[92vw]">
            <DialogHeader>
              <DialogTitle>{te("Importer des photos")}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{te("Dossier de destination")}</label>
                {creatingFolder ? (
                  <div className="flex gap-1.5">
                    <input
                      ref={newFolderInputRef}
                      autoFocus
                      type="text"
                      className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      placeholder={te("Nom du dossier")}
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); } }}
                      disabled={creatingFolderLoading}
                    />
                    <Button size="sm" variant="outline" onClick={handleCreateFolder} disabled={creatingFolderLoading || !newFolderName.trim()}>
                      {creatingFolderLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setCreatingFolder(false); setNewFolderName(''); }} disabled={creatingFolderLoading}>{te("Annuler")}</Button>
                  </div>
                ) : (
                  <div className="flex gap-1.5">
                    <Select value={selectedFolderId} onValueChange={setSelectedFolderId} disabled={uploading || uploadDone}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={te("Choisir un dossier")} />
                      </SelectTrigger>
                      <SelectContent>
                        {[...newlyCreatedFolders, ...allFolders.filter(af => !newlyCreatedFolders.some(nf => nf.id === af.id))].map((f) => (
                          <SelectItem key={f.id} value={String(f.id)}>{f.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreatingFolder(true)}
                      disabled={uploading || uploadDone}
                      title={te("Créer un nouveau dossier")}
                    >
                      <FolderPlus size={15} />
                    </Button>
                  </div>
                )}
              </div>

              {!uploadDone && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{te("Photos")}</label>
                  <input
                    ref={importFileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFilesSelected}
                  />
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploading) setDialogDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDialogDragOver(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDialogDragOver(false);
                      if (!uploading) addDroppedFiles(Array.from(e.dataTransfer.files));
                    }}
                    onClick={() => { if (!uploading) importFileRef.current?.click(); }}
                    className={`w-full min-h-[320px] rounded-xl border-2 border-dashed px-6 py-16 flex flex-col items-center justify-center text-center transition-colors cursor-pointer select-none ${
                      dialogDragOver
                        ? 'border-primary bg-primary/10 scale-[1.01]'
                        : 'border-primary/50 bg-primary/5 hover:border-primary hover:bg-primary/10'
                    }`}
                  >
                    <Upload size={48} className={`mb-4 ${dialogDragOver ? 'text-primary' : 'text-primary/70'}`} />
                    <p className="text-lg font-semibold text-foreground">
                      {dialogDragOver ? te("Déposez vos photos ici") : te("Glissez-déposez vos photos ici")}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">{te("ou cliquez pour parcourir votre ordinateur")}</p>
                  </div>
                </div>
              )}

              {selectedFiles.length > 0 && !uploadDone && (
                <div className="flex items-center justify-between rounded-md border border-input bg-muted/40 px-3 py-2">
                  <span className="text-sm font-medium">
                    {te("{{count}} photo sélectionnée", { count: selectedFiles.length })}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                      onClick={() => setSelectedFiles([])}
                      disabled={uploading}
                    >{te("Tout retirer")}</button>
                  </div>
                </div>
              )}

              {uploading && (
                <div className="space-y-1.5">
                  <Progress value={(uploadProgress / selectedFiles.length) * 100} />
                  <p className="text-xs text-muted-foreground text-center">
                    {te("{{sent}} / {{total}} photos envoyées", { sent: uploadProgress, total: selectedFiles.length })}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                {uploadDone ? (
                  <Button onClick={handleCloseImport}>Fermer</Button>
                ) : (
                  <Button
                    onClick={handleUpload}
                    disabled={uploading || !selectedFolderId || selectedFiles.length === 0}
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    Importer
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={20} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {(() => {
          const renderPicture = (pic: Picture) => {
            const isUsed = pictureToUse.has(pic.id);
            const isFav = isPicForce(pic);
            return (
              <div key={pic.id} className="relative group aspect-square">
                <button
                  onPointerDown={recordTapStart}
                  onPointerUp={(e) => handlePicturePointerUp(e, pic)}
                  onClick={() => onAddImage(pic.full_url, pic.width / pic.height, pic.id, pic.thumb_url)}
                  draggable
                  onMouseEnter={() => { const preload = new Image(); preload.src = pic.full_url; }}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/yearbook-image', JSON.stringify({ url: pic.full_url, ratio: pic.width / pic.height, pictureId: pic.id, thumbUrl: pic.thumb_url }));
                    e.dataTransfer.effectAllowed = 'copy';
                    const imgEl = e.currentTarget.querySelector('img');
                    if (imgEl) e.dataTransfer.setDragImage(imgEl, 30, 30);
                  }}
                  className="w-full h-full rounded overflow-hidden border border-editor-sidebar-border hover:border-editor-sidebar-active transition-colors cursor-grab active:cursor-grabbing touch-manipulation"
                >
                  <img src={pic.thumb_url} alt="" className="w-full h-full object-cover pointer-events-none" loading="lazy" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleFavori(pic); }}
                  className="absolute top-0.5 left-0.5 p-0.5 rounded transition-opacity opacity-0 group-hover:opacity-100"
                  style={isFav ? { opacity: 1 } : undefined}
                  title={te("Favori")}
                >
                  <Heart size={14} className={isFav ? 'fill-red-500 text-red-500' : 'text-white drop-shadow'} />
                </button>
                {isUsed && <div className="absolute inset-0 rounded bg-primary/50 pointer-events-none" />}
                {!isUsed && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(pic); }}
                    className="absolute top-0.5 right-0.5 p-0.5 rounded bg-destructive/80 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    title={te("Supprimer")}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
                {pic.quality && <QualityBadge quality={pic.quality} />}
                {pic.description && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-0.5 left-0.5 p-0.5 rounded bg-accent/90 text-accent-foreground transition-opacity opacity-0 group-hover:opacity-100"
                        title={te("Description disponible")}
                      >
                        <FileText size={12} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="right" className="w-56 p-3 space-y-2">
                      <p className="text-xs font-medium text-foreground">{te("Description")}</p>
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap">{pic.description}</p>
                      <Button size="sm" className="w-full text-xs" onClick={() => onAddText('text', pic.description)}>{te("Placer dans la page")}</Button>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            );
          };

          const displayFolders = searchQuery.trim() ? Object.values(searchedFolders) : folders;

          if (searchQuery.trim() && displayFolders.length === 0) {
            return (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground">{te("Aucun album ne correspond à « {{query}} »", { query: searchQuery.trim() })}</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-1.5 text-xs text-primary hover:underline"
                >{te("Effacer la recherche")}</button>
              </div>
            );
          }

          return displayFolders.map((folder) => {
            const isExpanded = expandedFolders.has(folder.id);
            const pics = fullPictures[folder.id] ?? folder.pictures;
            const hasMore = !fullPictures[folder.id] && folder.pictures_count > folder.pictures.length;
            const subfolders = folder.subfolders ?? [];

            return (
              <div key={folder.id} className="space-y-1">
                <button
                  onClick={() => toggleFolder(folder.id)}
                  className="w-full flex items-center gap-1.5 py-1.5 px-1 rounded text-sm text-editor-sidebar-foreground hover:bg-editor-sidebar-hover transition-colors"
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="font-medium truncate flex-1 text-left">{folder.title}</span>
                  {loadingSearchFolder === folder.id ? (
                    <Loader2 size={12} className="animate-spin text-muted-foreground" />
                  ) : (
                    <span className="text-[10px] text-muted-foreground">{folder.pictures_count}</span>
                  )}
                </button>

                {isExpanded && (
                  <div className="space-y-2">
                    {subfolders.map((sub) => {
                      const key = `${folder.id}:${sub.id}`;
                      const subExpanded = expandedSubfolders.has(key);
                      const subPics = fullSubPictures[key] ?? sub.pictures;
                      const subHasMore = !fullSubPictures[key] && sub.pictures_count > sub.pictures.length;
                      return (
                        <div key={sub.id} className="pl-3 border-l border-editor-sidebar-border ml-1 space-y-1">
                          <button
                            onClick={() => toggleSubfolder(folder.id, sub.id)}
                            className="w-full flex items-center gap-1.5 py-1 px-1 rounded text-xs text-editor-sidebar-foreground/90 hover:bg-editor-sidebar-hover transition-colors"
                          >
                            {subExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            <span className="truncate flex-1 text-left">{sub.title}</span>
                            <span className="text-[10px] text-muted-foreground">{sub.pictures_count}</span>
                          </button>
                          {subExpanded && (
                            <div className="space-y-2">
                              {subPics.length > 0 && (
                                <div className="grid grid-cols-3 gap-1.5 px-1">
                                  {subPics.map(renderPicture)}
                                </div>
                              )}
                              {subHasMore && (
                                <button
                                  onClick={() => loadAllSubPictures(folder.id, sub.id)}
                                  disabled={loadingSubfolder === key}
                                  className="w-full py-1.5 text-xs text-editor-sidebar-active hover:underline flex items-center justify-center gap-1 disabled:opacity-50"
                                >
                                  {loadingSubfolder === key ? <Loader2 size={12} className="animate-spin" /> : null}
                                  {te("Voir plus ({{count}} restantes)", { count: sub.pictures_count - sub.pictures.length })}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {pics.length > 0 && (
                      <div className="grid grid-cols-3 gap-1.5 px-1">
                        {pics.map(renderPicture)}
                      </div>
                    )}

                    {hasMore && (
                      <button
                        onClick={() => loadAllPictures(folder.id)}
                        disabled={loadingFolder === folder.id}
                        className="w-full py-1.5 text-xs text-editor-sidebar-active hover:underline flex items-center justify-center gap-1 disabled:opacity-50"
                      >
                        {loadingFolder === folder.id ? <Loader2 size={12} className="animate-spin" /> : null}
                        {te("Voir plus ({{count}} restantes)", { count: folder.pictures_count - folder.pictures.length })}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          });
        })()}


        {/* Sentinel for folders infinite scroll */}
        <div ref={foldersSentinelRef}>
          {loading && folders.length > 0 && (
            <div className="flex items-center justify-center py-2">
              <Loader2 size={14} className="animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{te("Supprimer cette photo ?")}</AlertDialogTitle>
              <AlertDialogDescription>{te("Cette action est irréversible. La photo sera définitivement supprimée.")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>{te("Annuler")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeletePicture} disabled={deleting}>
                {deleting ? <Loader2 size={14} className="animate-spin mr-1" /> : null}{te("Supprimer")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default PhotosPanel;